import React, { useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { Dropdown } from "react-bootstrap";
import { useParams, Link } from "react-router";
import { createBrandPerformanceColumns } from "./utility";

import {
  FILTER_LABEL,
  emptyString,
  pathConstant,
  loginConstants,
  sessionStorageKey,
  buttonGroupConstant,
  brandPerformanceConstants,
  averageCardTitleConstants,
  viewPathConstants,
  brandProfileConstants,
} from "../../constants";
import { setItemToStorage } from "../../../../system";
import {
  useSpinner,
  Spinner,
  DataTable,
  StatusCard,
  ChangeComponent,
  AverageRatingCard,
  isSuperAdmin,
  isSuperOrBrandAdmin,
  SearchComponent,
  calculateChangeColumnValueForSentimentRating,
  calculateChangeColumnValueForTransactionRating,
} from "../../../../shared";

const {
  EXPORT_DETAILED_CUSTOMER_SURVEY_DATA,
  GET_RECORD_PAGINATION_RESPONSES,
  GET_AVERAGE_RATING_RESPONSES,
  ALL_RESTAURANT_RESPONSE_DATA,
  ALL_GROUP_RESPONSE_DATA,
} = brandPerformanceConstants;
const { AVERAGE_TRANSACTION_RATING, AVERAGE_SENTIMENT_RATING } =
  averageCardTitleConstants;

//Displaying the general data of a brand and its survey responses in charts
const Performance = (props) => {
  const {
    isGroup,
    getData,
    sorting,
    dayCounts,
    tableTitle,
    exportFunc,
    searchString,
    setSearchString,
    selectedOption,
    sentimentTitle,
    transactionTitle,
    totalResponses,
    totalRestaurants,
    selectedResponse,
    userResponsesData,
    userResponseDataPagination,
    avgSentimentAndTransactionRatingData,
    openExportCustomerDataConfirmationModal,
  } = props;

  const { brandId } = useParams();

  // Show loading while a request is running.
  const spinner = useSpinner();

  //Handle store name click
  const handleClickStoreName = (restaurantData) => {
    setItemToStorage(loginConstants.LOCATION_ID, restaurantData.id);
    setItemToStorage(loginConstants.BRAND_ID, brandId);
    setItemToStorage(
      brandProfileConstants.LOCATION_STATE,
      JSON.stringify({
        name: isSuperAdmin()
          ? pathConstant.STORE_PERFORMANCE
          : pathConstant.PERFORMANCE,
      }),
    );
  };

  useEffect(() => {
    if (userResponsesData && userResponsesData.length > 0) {
      calculateChangeColumnValueForSentimentRating(userResponsesData);
      calculateChangeColumnValueForTransactionRating(userResponsesData);
    }
    // eslint-disable-next-line
  }, [userResponsesData]);

  const cellNameRenderer = useCallback(
    ({ row }) => {
      const restaurantData = row.original;
      const restaurantName = restaurantData.name;

      if (restaurantName) {
        if (selectedResponse === buttonGroupConstant.STORE) {
          return (
            <Link
              className="table-links"
              to={`${
                isSuperOrBrandAdmin() ? viewPathConstants.STORES : emptyString
              }/dashboard/${brandId}/brand-performance/store-performance/${
                restaurantData.id
              }`}
              state={{
                name: isSuperAdmin()
                  ? pathConstant.STORE_PERFORMANCE
                  : pathConstant.PERFORMANCE,
              }}
              onClick={() => handleClickStoreName(restaurantData)}
            >
              {restaurantName}
            </Link>
          );
        }
        const groupPath = `/dashboard/${brandId}/brand-performance/group-performance-page/${restaurantData.id}`;
        return (
          <Link
            className="table-links"
            to={`${
              isSuperOrBrandAdmin() ? viewPathConstants.STORES : emptyString
            }${groupPath}`}
            state={{
              name: isSuperAdmin()
                ? pathConstant.STORE_PERFORMANCE
                : pathConstant.PERFORMANCE,
            }}
            onClick={() => {
              setItemToStorage(
                sessionStorageKey.FRANCHISEE__ID,
                restaurantData.id,
              );
            }}
          >
            {restaurantName}
          </Link>
        );
      }
      return <span />;
    },
    // eslint-disable-next-line
    [brandId, selectedResponse],
  );

  const headerTransactionRatingRenderer = useCallback(
    () => {
      return <span className="text-secondary">{transactionTitle}</span>;
    },
    [transactionTitle],
  );

  const headerSentimentRatingRenderer = useCallback(
    () => {
      return <span className="text-secondary">{sentimentTitle}</span>;
    },
    [sentimentTitle],
  );

  const headerSurveyCompletionDateRenderer = useCallback(
    () => {
      return (
        <span className="text-secondary">
          PREVIOUS {dayCounts === 1 ? `${dayCounts} DAY` : `${dayCounts} DAYS`}
        </span>
      );
    },
    // eslint-disable-next-line
    [dayCounts],
  );

  const cellTransactionChangeRenderer = useCallback(
    ({ row }) => {
      const {
        transactionChange,
        isTransactionChangeEqualToZero,
        isTransactionChangeGreaterThanZero,
      } = row.original;
      return (
        <ChangeComponent
          result={transactionChange}
          isValueEqualToZero={isTransactionChangeEqualToZero}
          isValueGreaterThanZero={isTransactionChangeGreaterThanZero}
        />
      );
    },
    // eslint-disable-next-line
    [],
  );

  const cellSentimentChangeRenderer = useCallback(
    ({ row }) => {
      const {
        sentimentChange,
        isSentimentChangeEqualToZero,
        isSentimentChangeGreaterThanZero,
      } = row.original;
      return (
        <ChangeComponent
          result={sentimentChange}
          isValueEqualToZero={isSentimentChangeEqualToZero}
          isValueGreaterThanZero={isSentimentChangeGreaterThanZero}
        />
      );
    },
    // eslint-disable-next-line
    [],
  );

  const Columns = useMemo(
    () => createBrandPerformanceColumns({
      selectedResponse,
      cellNameRenderer,
      headerTransactionRatingRenderer,
      headerSentimentRatingRenderer,
      headerSurveyCompletionDateRenderer,
      cellTransactionChangeRenderer,
      cellSentimentChangeRenderer,
    }),
    [selectedResponse, cellNameRenderer, headerTransactionRatingRenderer,
      headerSentimentRatingRenderer, headerSurveyCompletionDateRenderer,
      cellTransactionChangeRenderer, cellSentimentChangeRenderer],
  );

  return (
    <>
      {(spinner(ALL_RESTAURANT_RESPONSE_DATA) ||
        spinner(ALL_GROUP_RESPONSE_DATA) ||
        spinner(GET_RECORD_PAGINATION_RESPONSES) ||
        spinner(GET_AVERAGE_RATING_RESPONSES) ||
        spinner(EXPORT_DETAILED_CUSTOMER_SURVEY_DATA)) && <Spinner />}
      <div className="container-fluid pb-3">
        <div className=" d-md-flex pb-3 justify-content-between">
          <StatusCard
            title={`Total ${tableTitle}`}
            data={totalRestaurants}
            classNames="me-md-3 me-0 mb-md-0 mb-3"
            bodyClassNames="p-3"
          />
          <StatusCard
            title={
              !selectedOption.length ||
              selectedOption.some((item) => item.label === FILTER_LABEL.ALL)
                ? "Total Responses"
                : `Total ${selectedOption.map(
                    (item) => item.value,
                  )} Star Responses`
            }
            data={totalResponses}
            classNames="mb-md-0 mb-3"
            bodyClassNames="p-3 mb-0"
          />
        </div>
        <div className=" d-md-flex mb-3 justify-content-between mt-2">
          <AverageRatingCard
            isGroup={isGroup}
            dayCounts={dayCounts}
            bodyClassNames="px-3 pb-0"
            classNames="me-md-3 me-0 mb-3 mb-md-0"
            title={AVERAGE_TRANSACTION_RATING}
            averageRating={
              avgSentimentAndTransactionRatingData.averageTransactionRatings
            }
            previousAverageRating={
              avgSentimentAndTransactionRatingData.previousAverageTransactionRatings
            }
            averageRatingQuestionsData={
              avgSentimentAndTransactionRatingData.averageTransaction
            }
          />
          <AverageRatingCard
            isGroup={isGroup}
            dayCounts={dayCounts}
            bodyClassNames="px-3 pb-0"
            classNames="mb-md-0 mb-3"
            title={AVERAGE_SENTIMENT_RATING}
            averageRating={
              avgSentimentAndTransactionRatingData.averageSentimentRatings
            }
            previousAverageRating={
              avgSentimentAndTransactionRatingData.previousAverageSentimentRatings
            }
            averageRatingQuestionsData={
              Array.isArray(
                avgSentimentAndTransactionRatingData?.averageSentiment,
              )
                ? avgSentimentAndTransactionRatingData.averageSentiment.filter(
                    (item) => item?.sentimentQuestion !== undefined,
                  )
                : []
            }
          />
        </div>
        <div className="pb-3 text-end">
          <Dropdown>
            <Dropdown.Toggle
              data-testid="export-button"
              className="green-background-color green-border"
              disabled={!userResponseDataPagination.totalElements}
            >
              Export
            </Dropdown.Toggle>

            <Dropdown.Menu className="text-center shadow ">
              <Dropdown.Item onClick={exportFunc}>Data Table</Dropdown.Item>
              <Dropdown.Item onClick={openExportCustomerDataConfirmationModal}>
                Detailed Customer Survey Data
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
        <div className="notification-form card shadow">
          <div className="card-body px-0">
            <div className="d-flex justify-content-between">
              <h5 className=" responses px-4">Total {tableTitle}</h5>
              <SearchComponent
                searchString={searchString}
                setSearchString={setSearchString}
              />
            </div>
            <div className="mt-3">
              <DataTable
                columns={Columns}
                data={userResponsesData}
                totalRows={userResponseDataPagination.totalElements}
                numberOfRowsPerPage={userResponseDataPagination.size}
                pageNumber={userResponseDataPagination.number + 1}
                pageSize={userResponseDataPagination.size}
                sortedBy={sorting}
                onPageChange={(pageNum) =>
                  getData(pageNum, null, userResponseDataPagination.size)
                }
                onColumnSort={(sortBy) =>
                  getData(
                    userResponseDataPagination.number,
                    sortBy,
                    userResponseDataPagination.size,
                  )
                }
                onSizeChange={(size, pageNum) => {
                  getData(pageNum, null, size);
                }}
                isPaginationDropDownEnable={true}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default connect(null, null)(Performance);
